import * as Sentry from "@sentry/react";
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  Sparkles,
  X
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useState,
  type FC,
  type ReactNode
} from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";

import {
  dismissAnalyses,
  dismissAnalysis,
  foldInAnalyses,
  foldInAnalysis,
  getDraftById
} from "../../../api/drafts-v2";
import {
  GenerationStatus,
  type AvailableAnalysis,
  type Draft
} from "../../../api/types/draft";
import { useOnInvalidate } from "../../caseIde/context/CaseIdeContext";

type Props = {
  draftId: string;
  onFoldedIn?: () => void;
};

/** While a draft waits on, or was written ahead of, an analysis. */
const POLL_MS = 60_000;
/** While Casey revises, so the buttons come back soon after the version lands. */
const REVISING_POLL_MS = 15_000;

export const REVISING_TOOLTIP =
  "Casey Is Revising — Available When The New Version Lands";

const ALL = "__all__";

const labelOf = (key: string): string =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const nameOf = (entry: AvailableAnalysis): string =>
  entry.label ?? labelOf(entry.key);

/** Casey is writing the draft: the backend refuses a fold until it lands. */
const isRevising = (draft: Draft): boolean =>
  draft.status === GenerationStatus.GENERATING ||
  draft.display_status === "running";

/** A disabled button swallows pointer events, so the tooltip sits on a wrapper. */
const Gate: FC<{ blocked: boolean; children: ReactNode }> = ({
  blocked,
  children
}) =>
  blocked ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">{children}</span>
      </TooltipTrigger>
      <TooltipContent>{REVISING_TOOLTIP}</TooltipContent>
    </Tooltip>
  ) : (
    children
  );

/**
 * Above a draft written ahead of its key fact analyses: "Waiting for
 * analysis" while it is held, and New analysis available once one lands
 * (fold it in with the drafting agent, as tracked changes, or dismiss it).
 * Two or more collapse into one row that folds them all in a single pass.
 * Distinct from a stale fact field, which the integrity check raises.
 */
export const DraftAnalysisStrip: FC<Props> = ({ draftId, onFoldedIn }) => {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(() => {
    getDraftById(draftId)
      .then(setDraft)
      .catch((error) => Sentry.captureException(error));
  }, [draftId]);

  useEffect(load, [load]);
  useOnInvalidate(["drafts"], load);

  const waiting = draft?.status === GenerationStatus.WAITING_FOR_ANALYSIS;
  const ahead = (draft?.pending_analyses ?? []).length > 0;
  const available = draft?.analyses_available ?? [];
  const revising = draft ? isRevising(draft) : false;
  const heldBack = revising && available.length > 0;
  useEffect(() => {
    if (!waiting && !ahead && !heldBack) return undefined;
    const timer = window.setInterval(
      load,
      heldBack ? REVISING_POLL_MS : POLL_MS
    );
    return () => window.clearInterval(timer);
  }, [waiting, ahead, heldBack, load]);

  const run = async (
    busy: string,
    call: () => Promise<Draft>,
    folded: string | null
  ) => {
    setBusyKey(busy);
    try {
      setDraft(await call());
      if (folded) {
        toast.success(folded);
        onFoldedIn?.();
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "That didn't work. Try again."
      );
    } finally {
      setBusyKey(null);
    }
  };

  const foldOne = (entry: AvailableAnalysis) =>
    run(
      entry.key,
      () => foldInAnalysis(draftId, entry.key),
      `Folding in the ${nameOf(entry)} analysis as tracked changes.`
    );
  const dismissOne = (entry: AvailableAnalysis) =>
    run(entry.key, () => dismissAnalysis(draftId, entry.key), null);
  const keys = available.map((entry) => entry.key);
  const foldAll = () =>
    run(
      ALL,
      () => foldInAnalyses(draftId, keys),
      `Folding in ${keys.length} analyses as tracked changes, in one pass.`
    );
  const dismissAll = () => run(ALL, () => dismissAnalyses(draftId, keys), null);

  if (!draft) return null;

  if (waiting) {
    const names = (draft.waiting_for_analyses ?? [])
      .map((key) => draft.waiting_labels?.[key] ?? labelOf(key))
      .join(", ");
    return (
      <div
        data-testid="draft-waiting-strip"
        className="flex items-center gap-2 border-b border-line-1 bg-fill-1 px-3 py-1.5 text-sm text-fg-3"
      >
        <Clock className="size-3.5 shrink-0" />
        <span className="min-w-0 flex-1 truncate">
          Waiting for analysis{names ? `: ${names}` : ""}. The draft starts when
          it lands, or after 20 minutes.
        </span>
      </div>
    );
  }

  if (!available.length) {
    if (!draft.start_note || draft.status !== GenerationStatus.GENERATING) {
      return null;
    }
    return (
      <div className="flex items-center gap-2 border-b border-line-1 bg-fill-1 px-3 py-1.5 text-sm text-fg-3">
        <Clock className="size-3.5 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{draft.start_note}</span>
      </div>
    );
  }

  const blocked = busyKey !== null || revising;
  const many = available.length > 1;

  const row = (entry: AvailableAnalysis) => (
    <div
      key={entry.key}
      data-testid="new-analysis-row"
      className={`flex items-center gap-2 bg-accent-soft py-1.5 pr-3 text-sm text-fg ${
        many ? "pl-8" : "pl-3"
      }`}
    >
      {many ? null : (
        <Sparkles className="size-3.5 shrink-0 text-accent-seed" />
      )}
      <span className="min-w-0 flex-1 truncate">
        {many ? null : (
          <span className="font-medium">New analysis available: </span>
        )}
        <span className={many ? "font-medium" : undefined}>
          {nameOf(entry)}
        </span>
        {entry.short_answer ? (
          <span className="text-fg-3"> · {entry.short_answer}</span>
        ) : null}
      </span>
      <Gate blocked={revising}>
        <Button
          variant={many ? "secondary" : "primary"}
          size="xs"
          disabled={blocked}
          onClick={() => void foldOne(entry)}
        >
          {busyKey === entry.key ? (
            <Loader2 className="size-3 animate-spin" />
          ) : null}
          Fold In With Casey
        </Button>
      </Gate>
      <Button
        variant="ghost"
        size="icon-xs"
        className="text-fg-3"
        aria-label={`Dismiss the ${nameOf(entry)} analysis`}
        disabled={busyKey !== null}
        onClick={() => void dismissOne(entry)}
      >
        <X className="size-3" />
      </Button>
    </div>
  );

  return (
    <TooltipProvider>
      <div data-testid="new-analysis-strip" className="border-b border-line-1">
        {many ? (
          <div
            data-testid="new-analysis-summary"
            className="flex items-center gap-2 bg-accent-soft px-3 py-1.5 text-sm text-fg"
          >
            <Button
              variant="ghost"
              size="icon-xs"
              className="text-fg-3"
              aria-expanded={expanded}
              aria-label={
                expanded ? "Hide Each Analysis" : "Show Each Analysis"
              }
              onClick={() => setExpanded((open) => !open)}
            >
              {expanded ? (
                <ChevronDown className="size-3" />
              ) : (
                <ChevronRight className="size-3" />
              )}
            </Button>
            <Sparkles className="size-3.5 shrink-0 text-accent-seed" />
            <span className="min-w-0 flex-1 truncate">
              <span className="font-medium">
                {available.length} New Analyses Available
              </span>
              <span className="text-fg-3">
                {" · "}
                {available.map(nameOf).join(", ")}
              </span>
            </span>
            <Gate blocked={revising}>
              <Button
                variant="primary"
                size="xs"
                disabled={blocked}
                onClick={() => void foldAll()}
              >
                {busyKey === ALL ? (
                  <Loader2 className="size-3 animate-spin" />
                ) : null}
                Fold All In With Casey ({available.length})
              </Button>
            </Gate>
            <Button
              variant="ghost"
              size="xs"
              disabled={busyKey !== null}
              onClick={() => void dismissAll()}
            >
              Dismiss All
            </Button>
          </div>
        ) : null}
        {many && !expanded ? null : available.map(row)}
      </div>
    </TooltipProvider>
  );
};
